import type { ChartV2Props } from '@chartv2';
import { ChartV2 } from '@chartv2';
import { Accordion } from '@design/components/accordion/Accordion';
import { Alert, AlertType } from '@design/components/alert/Alert';
import { Badge } from '@design/components/badge/Badge';
import { Card } from '@design/components/card/Card';
import { Carousel } from '@design/components/carousel/Carousel';
import { GradientCard } from '@design/components/gradient-card/GradientCard';
import { Grid } from '@design/components/grid/Grid';
import { GridRow } from '@design/components/grid/GridRow';
import { KeyStats } from '@design/components/key-stats/KeyStats';
import { Markdown } from '@design/components/markdown';
import { OrderedList } from '@design/components/ordered-list/OrderedList';
import { ResizableHandle } from '@design/components/resizable/ResizableHandle';
import { ResizablePanel } from '@design/components/resizable/ResizablePanel';
import { ResizablePanelGroup } from '@design/components/resizable/ResizablePanelGroup';
import { Separator } from '@design/components/separator/Separator';
import {
  RevampTable,
  type RevampTableColumn,
  type RevampTableRow,
} from '@design/components/table';
import { TimeSeriesInfo } from '@design/components/time-series-info/TimeSeriesInfo';
import { Typography } from '@design/components/typography/Typography';
import { AssetOverviewDataProvider } from '@design/data/AssetOverviewDataProvider';
import { ChartV2DataProvider } from '@design/data/chart-v2-data-provider/ChartV2DataProvider';
import { ScreenerTableDataProvider } from '@design/data/screener-table-data-provider/ScreenerTableDataProvider';
import { WatchlistTableDataProvider } from '@design/data/watchlist-table-data-provider/WatchlistTableDataProvider';
import { PriceDisplay } from '@design/widgets/price-display/PriceDisplay';
import {
  SimplifiedCalendar,
  type SimplifiedCalendarProps,
} from '@design/widgets/simplified-calendar/SimplifiedCalendar';
import type { ComponentType, ReactNode } from 'react';
import { Fragment } from 'react';
import { Row } from './Row';
import { renderComponent } from './renderComponent';
import { Stack } from './Stack';
import type {
  AccordionBlockProps,
  AlertBlockProps,
  AssetOverviewHeaderBlockProps,
  BadgeBlockProps,
  CardBlockProps,
  ChartBlockProps,
  ChartV2DataProviderBlockProps,
  GenerativeUiBlockName,
  GenerativeUiBlockPropsMap,
  GradientCardBlockProps,
  KeyStatsBlockProps,
  OrderedListBlockProps,
  PriceDisplayBlockProps,
  ResizableBlockProps,
  ScreenerTableDataProviderBlockProps,
  SimplifiedCalendarBlockProps,
  TableBlockColumn,
  TableBlockProps,
  TimeSeriesInfoBlockProps,
  TypographyBlockProps,
  WatchlistTableDataProviderBlockProps,
} from './schema';

/**
 * Adapter so the producer can supply ISO strings (which survive JSON
 * round-tripping) instead of `Date` instances.
 */
const TimeSeriesInfoBlock = ({
  from,
  to,
  ...rest
}: TimeSeriesInfoBlockProps) => (
  <TimeSeriesInfo {...rest} from={new Date(from)} to={new Date(to)} />
);

/**
 * Adapter that wraps `ChartV2` in a sized container. ChartV2's canvas
 * uses a `ResizeObserver` on its parent — without an explicit height
 * the chart collapses vertically. Mirrors the `ChartV2Frame` decorator
 * used in the `ChartV2` stories.
 */
const ChartBlock = (props: ChartBlockProps) => {
  const chartProps: ChartV2Props = props;

  return (
    <div
      style={{
        width: '100%',
        height: 480,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <ChartV2 {...chartProps} />
    </div>
  );
};

const ChartV2DataProviderBlock = (payload: ChartV2DataProviderBlockProps) => (
  <ChartV2DataProvider {...payload} />
);

const AssetOverviewHeader = (props: AssetOverviewHeaderBlockProps) => {
  return <AssetOverviewDataProvider {...props} />;
};

/**
 * Adapter that auto-stacks `Card` children with a token-aligned
 * vertical gap. Mirrors the top-level wrapping done in
 * `renderComponent` so the producer doesn't have to wrap every
 * card body in an explicit `Stack`.
 */
const CardBlock = ({
  children,
  ...rest
}: CardBlockProps & { children?: ReactNode }) => (
  <Card {...rest}>
    <Stack gap="md">{children}</Stack>
  </Card>
);

/**
 * Adapter exposing a JSON-friendly subset of `Badge`. The underlying
 * `label` and `leftSection` props accept any `ReactNode`; here we
 * constrain `label` to a `string` and drop `leftSection` entirely so
 * the generator only emits primitives.
 */
const BadgeBlock = (props: BadgeBlockProps) => <Badge {...props} />;

const TypographyBlock = (props: TypographyBlockProps) => (
  <Typography {...props} />
);

/**
 * Adapter exposing a JSON-friendly subset of `PriceDisplay`. Drops
 * `timeNode: ReactNode` (everything else is already a primitive).
 */
const PriceDisplayBlock = (props: PriceDisplayBlockProps) => (
  <PriceDisplay {...props} />
);

/**
 * Adapter exposing string-only title/message content and no agent-controlled
 * callbacks, custom icons, or action buttons.
 */
const alertTypeMap: Record<NonNullable<AlertBlockProps['type']>, AlertType> = {
  success: AlertType.Success,
  warning: AlertType.Warning,
  error: AlertType.Error,
  info: AlertType.Info,
};

const AlertBlock = ({ type, ...rest }: AlertBlockProps) => (
  <Alert {...rest} type={type ? alertTypeMap[type] : undefined} />
);

const GradientCardBlock = (props: GradientCardBlockProps) => (
  <GradientCard {...props} />
);

const KeyStatsBlock = (props: KeyStatsBlockProps) => <KeyStats {...props} />;

const OrderedListBlock = (props: OrderedListBlockProps) => (
  <OrderedList {...props} />
);

const WatchlistTableDataProviderBlock = (
  props: WatchlistTableDataProviderBlockProps
) => <WatchlistTableDataProvider {...props} />;

const ScreenerTableDataProviderBlock = (
  props: ScreenerTableDataProviderBlockProps
) => <ScreenerTableDataProvider {...props} />;

const DEFAULT_SIMPLIFIED_CALENDAR_LOCALES: SimplifiedCalendarProps['locales'] =
  {
    today: 'Today',
    noEvents: 'No events',
    showMore: count => `Show ${count} more`,
    emptyIllustrationAlt: 'No calendar events',
    emptyTitle: 'No upcoming events',
    emptyDescription: 'There are no events to show right now.',
  };

/**
 * Adapter exposing the date-grouped calendar without agent-controlled
 * callbacks. The renderer supplies the inert "show more" handler and the
 * `showMore` locale; producers only control JSON-safe categories, rows,
 * and locale labels.
 */
const SimplifiedCalendarBlock = ({
  categories,
  languageSetting = 'en-US',
  displayLimit,
  locales,
}: SimplifiedCalendarBlockProps) => (
  <div style={{ width: 'min(100%, 480px)', minHeight: 520 }}>
    <SimplifiedCalendar
      categories={categories}
      languageSetting={languageSetting}
      displayLimit={displayLimit}
      locales={{ ...DEFAULT_SIMPLIFIED_CALENDAR_LOCALES, ...locales }}
    />
  </div>
);

/**
 * Adapter that re-shapes `Accordion`'s rich `items` API into a
 * JSON-friendly form. Each item's `content` is a recursive tree of
 * generative-ui blocks, rendered through `renderComponent` so any
 * registered block can sit inside an accordion row.
 */
const AccordionBlock = ({ items, ...rest }: AccordionBlockProps) => (
  <Accordion
    {...rest}
    items={items.map(item => ({
      title: item.title,
      expandedByDefault: item.expandedByDefault,
      content: item.content
        ? renderComponent({ blocks: item.content })
        : undefined,
    }))}
  />
);

/**
 * Adapter that turns a plain `{ columns, rows }` shape into
 * `RevampTable` without changing the JSON contract. The first column
 * defaults to left-aligned (label / period style); subsequent columns
 * default to right-aligned (numeric style). Override per-column with
 * `alignment`.
 */
const TableBlock = ({
  columns,
  rows,
  withBodyRowHover,
  isHeaderSticky,
}: TableBlockProps) => {
  const columnAlignment = (col: TableBlockColumn, idx: number) =>
    col.alignment ?? (idx === 0 ? 'left' : 'right');

  const columnsMeta: RevampTableColumn[] = columns.map((col, i) => {
    const alignment = columnAlignment(col, i);

    return {
      id: col.key,
      title: col.label,
      type: 'text',
      alignment,
      isNumber: alignment === 'right',
    };
  });

  const data: RevampTableRow[] = rows.map(row =>
    columns.reduce<RevampTableRow>((acc, col) => {
      acc[col.key] = { value: row[col.key] ?? '' };

      return acc;
    }, {})
  );

  return (
    <RevampTable
      columnsMeta={columnsMeta}
      data={data}
      showBorder={false}
      stickyHeader={isHeaderSticky}
      withBodyRowHover={withBodyRowHover ?? true}
    />
  );
};

const ResizableBlock = ({
  orientation,
  panels,
  withHandle = true,
  disabled,
  height = 320,
}: ResizableBlockProps) => (
  <div
    style={{
      width: '100%',
      height,
      border: '1px solid var(--border-soft)',
    }}
  >
    <ResizablePanelGroup orientation={orientation} disabled={disabled}>
      {panels.map((panel, index) => (
        <Fragment key={index}>
          {index > 0 && <ResizableHandle withHandle={withHandle} />}
          <ResizablePanel
            defaultSize={panel.defaultSize}
            minSize={panel.minSize}
            maxSize={panel.maxSize}
            collapsible={panel.collapsible}
            collapsedSize={panel.collapsedSize}
          >
            {renderComponent({ blocks: panel.content })}
          </ResizablePanel>
        </Fragment>
      ))}
    </ResizablePanelGroup>
  </div>
);

/**
 * The single source of truth for which design-system components are
 * addressable from a generative-ui response. Adding a new renderable
 * block is a three-step change:
 *
 *  1. Add its name to `GENERATIVE_UI_BLOCK_NAMES` in `schema.ts`.
 *  2. Register the React component here.
 *  3. Document its purpose / props in `GenerativeUiOverview.mdx`.
 *
 * Components registered here MUST accept JSON-serializable props (no
 * `Date`, no functions, no React nodes other than via `children`); use
 * a thin adapter (see `TimeSeriesInfoBlock`) when the underlying
 * component needs a richer type.
 */
export type GenerativeUiRegistry = {
  [Name in GenerativeUiBlockName]: ComponentType<
    GenerativeUiBlockPropsMap[Name]
  >;
};

export const GENERATIVE_UI_REGISTRY: GenerativeUiRegistry = {
  Markdown,
  Chart: ChartBlock,
  ChartV2DataProvider: ChartV2DataProviderBlock,
  TimeSeriesInfo: TimeSeriesInfoBlock,
  AssetOverviewHeader,
  PriceDisplay: PriceDisplayBlock,
  Alert: AlertBlock,
  GradientCard: GradientCardBlock,
  KeyStats: KeyStatsBlock,
  OrderedList: OrderedListBlock,
  SimplifiedCalendar: SimplifiedCalendarBlock,
  Card: CardBlock,
  Badge: BadgeBlock,
  Accordion: AccordionBlock,
  Table: TableBlock,
  Resizable: ResizableBlock,
  Stack,
  Row,
  Grid,
  GridRow,
  Separator,
  Typography: TypographyBlock,
  Carousel,
  WatchlistTableDataProvider: WatchlistTableDataProviderBlock,
  ScreenerTableDataProvider: ScreenerTableDataProviderBlock,
};
